import { QrcodeOutlined } from "@ant-design/icons";
import { useMutation } from "@tanstack/react-query";
import { Alert, Button, Divider, Form, InputNumber, Select, Space, Typography } from "antd";
import PackageSelector from "components/PackageSelector";
import { useAuthenticatedRequestCreator } from "hooks/useRequests";
import { BatchType, BatchTypeNames, StaffPackage } from "models/packages";
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import "styles/staff/batch_assignment.css";

const { Text } = Typography;

interface BatchAssignmentForm {
    type: BatchType;
    pkg?: StaffPackage;
    count: number;
    created: boolean;
}

interface QRResult {
    qrUrl: string;
}

const TypeOptions = [
    { label: BatchTypeNames[BatchType.Scannable], value: BatchType.Scannable },
    { label: BatchTypeNames[BatchType.NonScannable], value: BatchType.NonScannable },
];

const useBatchAssignment = () => {
    const assign = useAuthenticatedRequestCreator<QRResult, BatchAssignmentForm>(
        (data) => ({
            method: 'post',
            url: '/packages/assign-batches',
            data: {
                packageId: data.pkg!.id,
                type: data.type,
                count: data.count
            }
        })
    );

    const getEstCount = useAuthenticatedRequestCreator<
        { count: number, created: boolean },
        { packageId: string, type: BatchType }>(
            ({ packageId, type }) => ({
                method: 'get',
                url: `/packages/${packageId}/est-batch-counts?type=${type}`
            })
        );

    const { mutateAsync: assignBatches, isLoading: isAssigning } = useMutation(assign);

    const { mutateAsync: getEstimatedCount, isLoading: isFetchingCount } = useMutation(getEstCount);

    return {
        assignBatches,
        getEstimatedCount,
        isLoading: isAssigning || isFetchingCount
    };
}

const PackageAlert = ({ owners, packageTypeInternalName, allowBatchAssignment }: StaffPackage) => {
    if (allowBatchAssignment) {
        return <Alert type="info" message={`${owners[0].firstName} ${owners[0].lastName}`} description={packageTypeInternalName} />;
    }

    return <Alert type="error" message="This package does not allow batch assignment." />
}

const PackageBatchAssignment = () => {
    const [form] = Form.useForm<BatchAssignmentForm>();
    const { assignBatches, getEstimatedCount, isLoading } = useBatchAssignment();
    const [result, setResult] = useState<QRResult>();
    const [countFloor, setCountFloor] = useState(0); // staff can add to batch count, but not decrease it

    const { type, pkg, count, created } = Form.useWatch([], form) ?? {};

    useEffect(() => {
        if (type && pkg && pkg.allowBatchAssignment) {
            getEstimatedCount({ packageId: pkg.id, type })
                .then(({ count, created }) => {
                    form.setFieldsValue({ count, created });
                    setCountFloor(count);
                });
        } else {
            form.setFieldsValue({ count: 0, created: false });
            setCountFloor(0);
        }
    }, [type, pkg, form, getEstimatedCount]);

    const submit = async () => {
        var data = await form.validateFields();

        const result = await assignBatches(data);
        setResult(result);

        window.open(result.qrUrl, "_blank");
        form.setFieldsValue({ created: true });
    }

    return <Space direction="vertical" style={{ padding: '50px', width: '35rem' }}>
        <Text style={{ fontSize: '36px' }}>Assign Batches</Text>
        <Divider />
        <Form
            id="batch-assignment"
            form={form}
            layout="vertical"
            disabled={isLoading}
            onValuesChange={() => setResult(undefined)}
        >
            <Form.Item label="Batch Type" name="type" required>
                <Select
                    options={TypeOptions}
                    style={{ lineHeight: 2, fontSize: '28px' }}
                />
            </Form.Item>

            <div style={{ width: '100%', display: 'flex', alignItems: 'start', margin: '0 0 24px' }}>
                <Form.Item
                    label="Package ID"
                    name="pkg"
                    style={{ flex: 1, marginRight: '20px' }}
                    help={pkg
                        ? <PackageAlert {...pkg} />
                        : undefined
                    }
                    trigger="onSelected"
                    required>
                    <PackageSelector
                        onSelected={pkg => form.setFieldsValue({ pkg })}
                        onClear={() => form.setFieldsValue({ pkg: undefined })}
                    />
                </Form.Item>
                <Form.Item
                    label="Batch Count"
                    name="count"
                    style={{ marginRight: '20px' }}
                    help={created
                        ? "Batches Exist"
                        : undefined
                    }
                    required>
                    <InputNumber disabled={!type || !pkg || !pkg.allowBatchAssignment || created} step={1} min={countFloor} />
                </Form.Item>
                <Button
                    onClick={submit}
                    size="large"
                    type="primary"
                    disabled={!type || !pkg || count <= 0}
                    style={{ height: '70px', width: '70px', marginTop: '31px' }}
                    icon={<QrcodeOutlined style={{ fontSize: '36px' }} />}>
                </Button>
            </div>

            {result && <Alert type="success"
                showIcon
                message={<Button size="large" type="link">
                    <Link to={result.qrUrl} target="_blank" rel="noreferrer">{BatchTypeNames[type]} Batch QR Codes</Link>
                </Button>}
            />}

            <Form.Item name="created" hidden></Form.Item>
        </Form>
    </Space>;
}

export default PackageBatchAssignment;